import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale } from '../../../i18n'
import { formatCurrency } from '../../helpers/formatters'
import { customerMotionTransition } from '../customer-area'
import { toVisualStatusBadgeClasses, toVisualStatusLabel } from './ordersListShared'
import type { FilteredOrderRow, TranslateFn } from './ordersListSectionTypes'

type OrderResultRowProps = {
  row: FilteredOrderRow
  rowIndex: number
  reducedMotion: boolean
  locale: ShopLocale
  language: ShopLanguage
  dateFormatter: Intl.DateTimeFormat
  onOpenOrderDetail: (orderId: string) => void
  onBuyAgain: (order: CustomerOrder) => void
  onRequestSupport: (order: CustomerOrder) => void
  t: TranslateFn
}

const resolvePaymentStateLabel = (paymentState: CustomerOrder['paymentState'], language: ShopLanguage): string => {
  if (language === 'it') {
    if (paymentState === 'paid') return 'Pagamento confermato'
    if (paymentState === 'pending') return 'Pagamento in attesa'
    if (paymentState === 'failed') return 'Pagamento non riuscito'
    if (paymentState === 'cancelled') return 'Pagamento annullato'
    return 'Pagamento non completato'
  }

  if (paymentState === 'paid') return 'Payment confirmed'
  if (paymentState === 'pending') return 'Payment pending'
  if (paymentState === 'failed') return 'Payment failed'
  if (paymentState === 'cancelled') return 'Payment cancelled'
  return 'Payment not completed'
}

export const OrderResultRow = ({
  row,
  rowIndex,
  reducedMotion,
  locale,
  language,
  dateFormatter,
  onOpenOrderDetail,
  onBuyAgain,
  onRequestSupport,
  t,
}: OrderResultRowProps) => {
  const { order, visualStatus, createdTimestamp } = row
  const createdDateLabel = createdTimestamp > 0 ? dateFormatter.format(new Date(createdTimestamp)) : t('common.never')
  const primaryItem = order.items[0] ?? null
  const mainProductLabel = primaryItem?.productName?.trim() || t('customer.orders.productFallback')
  const mainProductWithQuantity =
    primaryItem && primaryItem.quantity > 1
      ? `${mainProductLabel} (${primaryItem.quantity} ${language === 'it' ? 'pz' : 'pcs'})`
      : mainProductLabel
  const mainProductInitial = (mainProductLabel.trim()[0] || '#').toUpperCase()
  const additionalProductsCount = Math.max(order.items.length - 1, 0)
  const paymentMethodLabel = order.paymentMethodLabel?.trim() || t('customer.orders.paymentMethodFallback')
  const paymentStateLabel = resolvePaymentStateLabel(order.paymentState, language)
  const rowTransition = reducedMotion
    ? { duration: 0 }
    : {
        ...customerMotionTransition,
        duration: 0.26,
        delay: Math.min(rowIndex * 0.05, 0.2),
      }

  return (
    <motion.li
      layout
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
      transition={rowTransition}
    >
      <motion.article
        role="button"
        tabIndex={0}
        onClick={() => onOpenOrderDetail(order.id)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpenOrderDetail(order.id)
          }
        }}
        whileHover={reducedMotion ? undefined : { y: -1, scale: 1.005 }}
        whileTap={reducedMotion ? undefined : { scale: 0.995 }}
        transition={customerMotionTransition}
        className="ca-theme-transition cursor-pointer rounded-xl border border-(--color-border-default) bg-[color-mix(in_srgb,var(--color-bg-card)_96%,var(--color-bg-surface-muted))] px-4 py-4 shadow-(--shadow-sm) transition-[transform,box-shadow,border-color,background-color] duration-200 hover:border-(--color-border-strong) hover:bg-(--color-bg-card) hover:shadow-(--shadow-md) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_32%,transparent)] sm:px-5"
      >
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-(--color-text-muted)">
                #{order.orderNumber}
              </p>
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${toVisualStatusBadgeClasses(
                  visualStatus,
                )}`}
              >
                {toVisualStatusLabel(visualStatus, language)}
              </span>
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-(--color-border-default) bg-(--color-bg-surface-muted) text-[11px] font-semibold text-(--color-text-secondary)">
                {mainProductInitial}
              </span>
              <p className="truncate text-[0.95rem] font-semibold text-(--color-text-primary)">{mainProductWithQuantity}</p>
            </div>
            {additionalProductsCount > 0 ? (
              <p className="mt-0.5 text-xs text-(--color-text-secondary)">
                +{additionalProductsCount} {t('customer.orders.productsLabel')}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-(--color-text-muted)">
              {t('customer.orders.paymentMethod')}: {paymentMethodLabel}
            </p>
            {order.paymentState !== 'paid' ? (
              <p className="mt-0.5 text-xs text-(--color-status-warning)">{paymentStateLabel}</p>
            ) : null}
          </div>

          <div className="text-left sm:text-right">
            <p className="text-xs text-(--color-text-muted)">{createdDateLabel}</p>
            <p className="mt-1 text-lg font-semibold text-(--color-text-primary)">
              {formatCurrency(order.total, locale)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <motion.button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onOpenOrderDetail(order.id)
            }}
            whileTap={reducedMotion ? undefined : { scale: 0.985 }}
            transition={customerMotionTransition}
            className="ca-btn-primary inline-flex h-8 items-center px-3 text-xs font-semibold"
          >
            {t('customer.orders.detailCta')}
          </motion.button>
          <motion.button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onBuyAgain(order)
            }}
            whileTap={reducedMotion ? undefined : { scale: 0.985 }}
            transition={customerMotionTransition}
            className="ca-btn-secondary inline-flex h-8 items-center px-3 text-xs font-semibold"
          >
            {t('customer.orders.rebuyCta')}
          </motion.button>
          <motion.button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onRequestSupport(order)
            }}
            whileTap={reducedMotion ? undefined : { scale: 0.985 }}
            transition={customerMotionTransition}
            className="ca-btn-tertiary inline-flex h-8 items-center px-2.5 text-xs font-semibold"
          >
            {t('customer.orders.supportCta')}
          </motion.button>
        </div>
      </motion.article>
    </motion.li>
  )
}
